import { useEffect, useRef, useState, KeyboardEvent } from 'react';
import { Check, ChevronDown, ChevronUp, Layers, Zap, LucideIcon } from 'lucide-react';
import { Modo, MODOS, modoLabel } from '../../utils/modo';

const ICONS: Record<Modo, LucideIcon> = { quick: Zap, deeper: Layers };

interface ModoSelectorProps {
  value: Modo;
  onChange: (value: Modo) => void;
}

/**
 * Selector de modo en el compositor (maqueta 2026-10-14-modos-respuesta, aprobada): texto «Quick answer ⌄» que abre
 * hacia arriba un menú de dos opciones con su línea de ayuda. Escape y un clic fuera lo cierran y devuelven el foco.
 */
export default function ModoSelector({ value, onChange }: ModoSelectorProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const close = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) buttonRef.current?.focus();
  };

  useEffect(() => {
    if (!open) return;
    optionRefs.current[Math.max(0, MODOS.findIndex(m => m.value === value))]?.focus();
    const onMouseDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const select = (modo: Modo) => {
    onChange(modo);
    close(true);
  };

  const onMenuKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close(true);
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const i = optionRefs.current.findIndex(el => el === document.activeElement);
      const next = (i + (e.key === 'ArrowDown' ? 1 : -1) + MODOS.length) % MODOS.length;
      optionRefs.current[next]?.focus();
    }
  };

  const Chevron = open ? ChevronUp : ChevronDown;

  return (
    <div
      className="relative"
      ref={wrapperRef}
      onBlur={(e) => { if (open && !wrapperRef.current?.contains(e.relatedTarget as Node | null)) setOpen(false); }}
    >
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Modo de respuesta: ${modoLabel(value)}`}
        className={`inline-flex items-center gap-[5px] h-[30px] pl-2.5 pr-2 rounded-lg text-[13px] font-medium transition-colors motion-reduce:transition-none focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 ${
          open ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
        }`}
      >
        {modoLabel(value)}
        <Chevron className="w-3.5 h-3.5" strokeWidth={1.8} />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Modo de respuesta"
          onKeyDown={onMenuKeyDown}
          className="absolute right-0 bottom-full mb-3 w-[300px] p-1.5 bg-white rounded-[10px] z-30 text-left shadow-[0_12px_32px_rgba(15,23,42,0.12),0_0_0_1px_rgba(15,23,42,0.06)]"
        >
          {MODOS.map((m, i) => {
            const selected = m.value === value;
            const Icon = ICONS[m.value];
            return (
              <button
                key={m.value}
                ref={el => { optionRefs.current[i] = el; }}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                onClick={() => select(m.value)}
                className="w-full flex items-start gap-2.5 px-2.5 py-[9px] rounded-lg text-left hover:bg-slate-50 focus:bg-slate-50 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600"
              >
                <span className={`w-7 h-7 rounded-lg flex-none inline-flex items-center justify-center ${selected ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-100 text-slate-600'}`}>
                  <Icon className="w-4 h-4" strokeWidth={1.8} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[13.5px] font-medium text-slate-900">{m.label}</span>
                  <span className="block text-xs leading-[1.4] text-slate-500 mt-px">{m.help}</span>
                </span>
                {selected && <Check className="w-4 h-4 mt-1.5 text-indigo-600 flex-none" strokeWidth={1.8} aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
